import axios from 'axios';

// API 기본 설정
const API_BASE_URL = process.env.REACT_APP_CAMPUS_API_BASE_URL || 'https://medaicampus.kr/gateway';

// axios 인스턴스 생성
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000,
  withCredentials: true,
});

// 요청 인터셉터 - 모든 요청에 토큰 자동 첨부
apiClient.interceptors.request.use(
  async (config) => {
    const url = config.url || '';
    
    // GET 요청에 대한 공개 API 경로들 - 토큰 없이도 접근 가능한 API
    const publicPaths = [
      // 강의 관련
      '/api/lectures/info/list',
      '/api/lectures/info/',
      '/api/lectures/',
      // 섹션 관련
      '/api/sections/list',
      '/api/sections/',
      // 검색 관련
      '/api/search/lectures',
      '/api/search/mentors', 
      '/api/search/posts',
      '/api/lecturers/search',
      // 게시글 관련 (목록만 공개, 상세는 비공개)
      '/api/posts/list',
      // 기본 UI 및 Admin UI
      '/api/basic-ui',
      '/api/admin/ui/basic',
      // 인증 관련
      '/auth/sign-in', 
      '/auth/public-key',
      '/api/auth/public-key',
      '/api/auth/check-email-duplication',
      '/api/auth/sign-up/check-email',
      '/api/auth/send-email-verification',
      '/api/auth/verify-email',
      '/api/auth/reset-password',
      '/api/auth/sign-up',
      // 스터디 카테고리
      '/api/study-categories/list'
    ];
    
    // GET 요청이고 공개 API 경로인 경우 또는 모든 인증 관련 API는 토큰 로직 스킵
    const isGetRequest = config.method?.toLowerCase() === 'get';
    const isPublicApi = publicPaths.some((p) => url.includes(p));
    const isAuthApi = url.includes('/auth/') || url.includes('/api/auth/');
    
    if ((isGetRequest && isPublicApi) || isAuthApi) {
      /// console.log('공개 API 또는 인증 API 요청, 토큰 없이 진행:', url);
      const token = localStorage.getItem('access_token');
      if (token && config.headers && !isAuthApi) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    }

    // 기존 토큰이 있으면 헤더에 추가
    const token = localStorage.getItem('access_token');
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터 - 401 에러 시 토큰 갱신 시도
apiClient.interceptors.response.use(
  (response) => {
    // 서버에는 별도의 /api/auth/refresh 엔드포인트가 없습니다.
    // JwtAuthFilter가 access token이 만료/누락되면 refresh 쿠키로 토큰을 자동 재발급하고,
    // 새 access token을 'Authorization' 응답 헤더(+ 'X-Token-Refreshed: true')로 내려줍니다.
    // 여기서 그 새 토큰을 받아 로컬 스토리지를 갱신합니다.
    // ※ 운영(cross-origin) 환경에서는 nginx가 아래 헤더를 노출해야 읽을 수 있습니다:
    //   Access-Control-Expose-Headers: Authorization, X-Token-Refreshed
    const refreshed = response.headers?.['x-token-refreshed'];
    const authHeader = response.headers?.['authorization'];
    if (refreshed && authHeader) {
      const newToken = authHeader.replace(/^Bearer\s+/i, '').trim();
      if (newToken) {
        localStorage.setItem('access_token', newToken);
      }
    }
    return response;
  },
  async (error) => {
    // 네트워크 에러 처리
    if (!error.response) {
      /// console.error('네트워크 에러:', error.message);
      return Promise.reject(new Error('네트워크 연결을 확인해주세요.'));
    }
    
    // 401 에러 시 토큰 제거 - 강의 시청 중에는 제거하지 않음
    if (error.response?.status === 401) {
      const url = error.config?.url || '';
      const isLectureProgressApi = url.includes('/api/enrollments/') || url.includes('/api/me/progress');
      
      // 강의 진행률 관련 API는 401 에러가 발생해도 토큰을 제거하지 않음
      // (강의 시청 중 토큰 만료 방지)
      if (!isLectureProgressApi) {
        /// console.log('401 에러 발생, 토큰 제거 (강의 진행률 API 아님)');
        localStorage.removeItem('access_token');
      } else {
        /// console.log('401 에러 발생했지만 강의 진행률 API이므로 토큰 유지');
      }
    }
    
    const status = error.response?.status;
    if (status >= 400 && status < 500) {
      const serverMessage = error.response?.data?.message;
      if (serverMessage) {
        return Promise.reject(new Error(serverMessage));
      }
    }

    return Promise.reject(error);
  }
);

export default apiClient;
export { API_BASE_URL };
